import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import screensImg from '../asset/screens.webp';
import ProjectCard from '../components/ProjectCard';
import LogoTicker from '../components/LogoTicker';
import newSection from '../asset/newSection.webp';
import Engineering from '../asset/engineering.webp'
import AI from '../asset/AI.webp'
import Partnership from '../asset/partnership.webp'
import Cloud from '../asset/cloud.webp'
import FaqItem from '../components/FaqItem'
import BlogCard from '../components/BlogCard';
import blogData from '../data/blog-data.json';
import Faq from './Faq';
import {
  FaMobileAlt,
  FaBolt,
  FaSearch,
  FaShieldAlt,
  FaChartLine,
  FaHandshake,
} from 'react-icons/fa';

import { servicesData } from '../data/servicesData';
import { testimonialsData } from '../data/testimonialsData';
import { projectsData } from '../data/projectsData';
import { homeStats } from '../data/homeData';

import { ArrowRight } from '../components/Icons';
import { Helmet } from 'react-helmet-async';

import webLottieMobile from '../asset/hero_webp2.webp';

import '../styles/home.css';

// ✅ Lazy load HeroLottie — Lottie library + JSON only downloaded on desktop
const HeroLottie = lazy(() => import('../pages/HeroLottie'));

const HERO_HEADING_PRIMARY = {
  lead: 'Web Development',
  accent: ' Services',
};

const HERO_HEADING_ROTATING = [
  { lead: 'App Development', accent: ' Services' },
  { lead: 'Software Development', accent: ' Services' },
];

const HERO_HEADING_STEP_COUNT = 1 + HERO_HEADING_ROTATING.length;

export default function Home() {
  // ✅ Reactive isMobile — updates on resize, no stale value
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [heroHeadingIndex, setHeroHeadingIndex] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setHeroHeadingIndex((i) => (i + 1) % HERO_HEADING_STEP_COUNT);
    }, 4000);
    return () => window.clearInterval(id);
  }, []);

  const rotatingSlide =
    heroHeadingIndex > 0 ? HERO_HEADING_ROTATING[heroHeadingIndex - 1] : null;

  return (
    <main className="home">
      <Helmet>
        <title>Web Development Services | High-Speed SEO Websites</title>
        <meta name="description" content="Professional web development services delivering fast, high-performance, and SEO-optimized websites designed to improve rankings" />
        <meta property="og:title" content="Web Development Services | High-Speed SEO Websites" />
        <meta property="og:description" content="Professional web development services delivering fast, high-performance, and SEO-optimized websites designed to improve rankings" />



        <script
          id="schema-page"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "WebPage",
                  "@id": "https://zenteratech.com/#webpage",
                  "url": "https://zenteratech.com/",
                  "name": "Web Development Services | High-Speed SEO Websites",
                  "isPartOf": {
                    "@id": "https://zenteratech.com/#website"
                  },
                  "about": {
                    "@id": "https://zenteratech.com/#organization"
                  },
                  "description": "Professional web development, app development, SEO, digital marketing, and software development services for modern businesses in the USA."
                },

                {
                  "@type": "Service",
                  "@id": "https://zenteratech.com/#services",
                  "serviceType": [
                    "Web Development",
                    "App Development",
                    "Software Development",
                    "Digital Marketing",
                    "SEO Services",
                    "Ecommerce Development"
                  ],
                  "provider": {
                    "@id": "https://zenteratech.com/#organization"
                  },
                  "areaServed": {
                    "@type": "Country",
                    "name": "United States"
                  },
                  "description": "ZenteraTech provides custom web development, mobile application development, SEO optimization, ecommerce solutions, branding, and digital marketing services for startups and businesses."
                }
              ]
            })
          }}
        />

        {/* ✅ Preload hero image only on mobile — desktop preloads nothing (uses Lottie) */}
        {isMobile && (
          <link
            rel="preload"
            as="image"
            href={webLottieMobile}
            imageSrcSet={`${webLottieMobile} 600w`}
            imageSizes="(max-width: 480px) 100vw, 600px"
            fetchPriority="high"
            decoding="async"
            loading="eager"
          />
        )}
              <link rel="canonical" href="https://zenteratech.com" />
      </Helmet>

      {/* ---- Hero ---- */}
      <section className="hero">

        <div className="hero-grid-overlay" />
        <div className="hero-bg-orb hero-orb-1" />
        <div className="hero-bg-orb hero-orb-2" style={{ animationDelay: '-5s' }} />
        <div className="hero-bg-orb hero-orb-3" style={{ animationDelay: '-10s' }} />
        <div className="hero-noise" />

        <div className="container hero-container">

          {/* Heading — direct grid child so it can sit above the visual on mobile */}
          <div className="hero-heading-slot">
            <div className="badge-dot-d">
              <span className="badge badge-hero">
                <span className="badge-dot" />
                Building the Future of Tech
              </span>
            </div>

            <div className="hero-title-stack">
              <h1
                className="hero-title"
                style={{
                  visibility: heroHeadingIndex === 0 ? 'visible' : 'hidden',
                  position: heroHeadingIndex === 0 ? 'relative' : 'absolute',
                  margin: 0,
                }}
              >
                {HERO_HEADING_PRIMARY.lead}
                <span className="hero-title-gradient">{HERO_HEADING_PRIMARY.accent}</span>
              </h1>
              <p
                className="hero-title"
                style={{
                  visibility: heroHeadingIndex !== 0 ? 'visible' : 'hidden',
                  position: heroHeadingIndex !== 0 ? 'relative' : 'absolute',
                  margin: 0,
                }}
                aria-hidden={heroHeadingIndex === 0}
              >
                {rotatingSlide && (
                  <span key={heroHeadingIndex} className="hero-title-rotating-text">
                    {rotatingSlide.lead}
                    <span className="hero-title-gradient">{rotatingSlide.accent}</span>
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Rest of hero content — subtitle, rating, buttons */}
          <div className="hero-content">

            <p className="hero-subtitle" style={{ fontWeight: 'bold', marginTop: '20px' }}>
              Transforming Ideas Into Powerful Digital Products<br />
              Custom Web, App & Software Solutions for Modern Businesses
            </p>

            {/* Social proof pill */}
            <div className="hero-rating-badge">
              <div className="rating-logos">
                <span className="rating-logo clutch">C</span>
                <span className="rating-logo google">G</span>
                <span className="rating-logo trustpilot">★</span>
              </div>
              <div className="rating-stars">
                <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
              </div>
              <div className="rating-info">
                <span className="rating-value">4.8 Rating</span>
                {/* <span className="rating-divider" /> */}
                {/* <span className="rating-count">50+ Reviews</span> */}
              </div>
            </div>

            <div className="hero-actions">
              <Link to="/services" className="btn-primary hero-btn-cta">
                Explore Services
                <ArrowRight />
              </Link>
              <Link to="/projects" className="btn-outline" id="btn-outline">
                View Our Work
              </Link>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="hero-visual animate-fade-in">
            <div className="hero-lottie-wrapper">
              {isMobile ? (
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${webLottieMobile} 600w`}
                    sizes="(max-width: 480px) 100vw, 600px"
                    fetchPriority="high"
                    decoding="async"
                    loading="eager"
                  />
                  <img
                    className="hero-lottie-mobile"
                    src={webLottieMobile}
                    alt="Web Development Services"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    width="600"
                    height="600"
                  />
                </picture>
              ) : (
                <Suspense fallback={null}>
                  <HeroLottie />
                </Suspense>
              )}
            </div>
          </div>

        </div>

        <div className="hero-bottom-fade" />
      </section>

      {/* ---- Hero Stats ---- */}
      <section className="hero-stats-section">
        <div className="hero-stats-container">
          <div className="hero-stats centered">
            {homeStats.map(s => (
              <div key={s.label} className="hero-stat">
                <span className="hero-stat-value">{s.value}</span>
                <span className="hero-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LogoTicker />

      {/* ---- About & Vision ---- */}
      <section className="section home-about-vision">
        <div className="container">
          <div className="home-about-grid">
            <div className="home-about-content">
              <h2 className="badge new-h2" >Who We Are</h2>
              <p className="section-title">Pioneering <span className="highlight">Digital Excellence</span></p>
              <p className="section-subtitle">
                Zentera Tech is a modern web development company focused on building fast, scalable, and user-friendly digital solutions for businesses of all sizes. From custom websites and ecommerce stores to web applications and digital growth strategies, we help brands create a strong online presence with clean design, reliable performance, and SEO-focused development.
              </p>
              <div className="home-vision-mission-cards">
                <div className="vision-mission-card glass">
                  <span className="card-badge pink">Our Vision</span>

                  <p>Our vision is to help businesses grow through innovative digital experiences, intelligent web technologies, and result-driven online strategies.</p>
                </div>
                <div className="vision-mission-card glass">
                  <span className="card-badge purple">Our Mission</span>

                  <p>Our mission is to empower startups, small businesses, and enterprises with high-quality web development, SEO, branding, app development, and digital marketing solutions tailored to their growth goals.</p>
                </div>
              </div>
            </div>
            <div className="home-about-image-wrapper">
              <div className="about-glass-image">
                <div className="about-img-glow" />
                <img
                  src={newSection}
                  alt="Innovation"
                  className="about-img animate-float"
                  width="600"
                  height="450"
                />
                <div className="about-floating-stat glass">
                  <span className="stat-num">99.9%</span>
                  <span className="stat-label">Uptime Guaranteed</span>
                </div>
                <div className="about-floating-tag">
                  <span className="tag-dot" />
                  AI-Powered
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ---- Intro Section ---- */}
      <section className="section intro-section">
        <div className="container">
          <div className="intro-grid">
            <div className="intro-left">
              <h2 className="badge new-h2">Web Development Service In US</h2>
              <p className="section-title">
                ZenTera Tech Delivers <span className="highlight">Modern Web Solutions</span>
              </p>
              <div className="intro-divider" />
              <div className="intro-stats-row">
                <div className="intro-stat">
                  <span className="intro-stat-value">150+</span>
                  <span className="intro-stat-label">Projects Delivered</span>
                </div>
                <div className="intro-stat">
                  <span className="intro-stat-value">98%</span>
                  <span className="intro-stat-label">Client Satisfaction</span>
                </div>
                <div className="intro-stat">
                  <span className="intro-stat-value">5★</span>
                  <span className="intro-stat-label">Average Rating</span>
                </div>
              </div>
            </div>
            <div className="intro-right">
              <p className="intro-body">
                ZenTera Tech delivers modern, high-performance <Link to='services/custom-web-development'><span style={{ textDecoration: 'underline' }}>web development services</span></Link> in the US
                designed to help businesses build a strong and lasting digital presence. We don't
                just create websites — we build fast, scalable, and conversion-focused digital
                experiences that help brands stand out in competitive markets.
              </p>
              <Link to="/contact" className="btn-primary intro-cta">
                Get a Free Consultation <ArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Screens Showcase ---- */}
      <section className="screens-showcase-section">
        <div className="screens-showcase-header">
          <span className="badge">Client Projects</span>
          <h2 className="screens-showcase-title">Products We've <span className="highlight">Brought to Life</span></h2>
          <p className="screens-showcase-sub">Real projects. Real results. From travel platforms to construction sites — we build digital experiences that work.</p>
        </div>
        <img
          src={screensImg}
          alt="Project screens showcase"
          className="screens-showcase-img"
          loading="lazy"
          width="1200"
          height="675"
        />

      </section>


      {/* ---- What You Get ---- */}
      <section className="section what-you-get-section">
        <div className="container">
          <div className="section-header center">
            <h2 className="badge new-h2">What You Get</h2>
            <p className="section-title">Complete Web Solutions <span className="highlight">Built for Growth</span></p>
            <p className="section-subtitle">
              ZenTera Tech delivers complete web development solutions designed to grow your business online.
            </p>
          </div>

          <div className="wyg-grid">

            {/* Left — description */}
            <div className="wyg-left">
              <p className="wyg-body">
                You get a fully responsive, fast, and SEO-optimized website built with clean and
                scalable code. Every project is tailored to your goals, ensuring a strong user
                experience and better conversions. We also focus on security, performance, and
                future-ready development so your website can grow with your business. Along with
                development, you get reliable support and a smooth, hassle-free experience from
                start to finish.
              </p>
              <Link to="/contact" className="btn-primary" style={{ marginTop: '8px', alignSelf: 'flex-start' }}>
                Start Your Project <ArrowRight />
              </Link>
            </div>

            {/* Right — feature cards */}
            <div className="wyg-right">
              {[
                {
                  icon: <FaMobileAlt />,
                  title: 'Fully Responsive Design',
                  desc: 'Pixel-perfect on every screen — desktop, tablet, and mobile.',
                },
                {
                  icon: <FaBolt />,
                  title: 'Fast & Optimized',
                  desc: 'Lightning-fast load times with clean, performance-first code.',
                },
                {
                  icon: <FaSearch />,
                  title: 'SEO-Ready Structure',
                  desc: 'Built with best-practice SEO from the ground up for better rankings.',
                },
                {
                  icon: <FaShieldAlt />,
                  title: 'Secure & Reliable',
                  desc: 'Enterprise-grade security with 99.9% uptime guaranteed.',
                },
                {
                  icon: <FaChartLine />,
                  title: 'Conversion Focused',
                  desc: 'Every element designed to turn visitors into paying customers.',
                },
                {
                  icon: <FaHandshake />,
                  title: 'Ongoing Support',
                  desc: 'We stay with you after launch — updates, fixes, and guidance.',
                },
              ].map((item, i) => (
                <div key={i} className="wyg-card">
                  <span className="wyg-icon">{item.icon}</span>

                  <div className="wyg-card-text">
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>



      {/* ---- Services ---- */}
      <section className="section services-section">
        <div className="container">
          <div className="section-header center">
            <h2 className="badge new-h2">Our Expertise</h2>
            <p className="section-title">Solutions Built to <span className="highlight">Scale With You</span></p>
            <p className="section-subtitle">
              Zentera Tech provides professional web development services tailored to modern business needs. Our services include custom website development, ecommerce solutions, Shopify and WordPress development, responsive web design, web application development, SEO optimization, and digital marketing support.

            </p>
          </div>
          <div className="services-grid">
            {servicesData.slice(0, 6).map(service => (
              <ServiceCard key={service.slug} service={service} />
            ))}
          </div>
          <div className="section-cta">
            <Link to="/services" className="btn-outline">
              View All 20+ Services
              <ArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ---- Code Innovation ---- */}
      <section className="section home-code-section">
        <div className="container">
          <div className="home-code-grid glass">
            <div className="code-content">
              <h2 className="badge new-h2">Our Approach</h2>
              <p className="section-title">Building the <span className="highlight">Future with Code</span></p>
              <p>
                We are more than just a website studio or marketing agency — we are your partners
                in digital transformation. Our goal is to help businesses thrive in an ever-evolving digital world.
              </p>
              <div className="code-content-metrics">
                <div className="code-metric">
                  <span className="code-metric-val">10×</span>
                  <span className="code-metric-label">Faster Delivery</span>
                </div>
                <div className="code-metric">
                  <span className="code-metric-val">99.9%</span>
                  <span className="code-metric-label">Reliability SLA</span>
                </div>
                <div className="code-metric">
                  <span className="code-metric-val">24/7</span>
                  <span className="code-metric-label">Support</span>
                </div>
              </div>
              <div>
                <Link to="/contact" className="btn-primary">Contact Us <ArrowRight /></Link>
              </div>
            </div>
            <div className="code-window">
              <div className="code-header">
                <div className="code-dots"><span /><span /><span /></div>
                <div className="code-tab">zentera-service.js</div>
                <div className="code-live-badge"><span className="live-dot" /> Live</div>
              </div>
              <pre className="code-body">
                <code>{`// ZenTera Core Integration
import { AI, Cloud } from '@zentera/core';

export const scaleBusiness = async (data) => {
  const insight = await AI.analyze(data);
  const infrastructure = await Cloud.deploy({
    scaling: 'auto',
    reliability: '99.99%',
    security: insight.recommendations
  });
  
  return { status: 'Optimized', infrastructure };
};`}</code>
              </pre>
              <div className="code-terminal-output">
                <span className="terminal-prompt">▶</span>
                <span className="terminal-text">Status: <span className="terminal-success">Optimized</span> · Deployed in 1.2s</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Why Us (Bento) ---- */}
      <section className="section why-bento-section">
        <div className="container">
          <div className="section-header center">
            <h2 className="badge new-h2">Why ZenTera Tech</h2>
            <p className="section-title">Your Technical <span className="highlight">Edge in a Digital World</span></p>
            <p className="section-subtitle">At Zentera Tech, we focus on delivering websites that are not only visually professional but also built for performance, scalability, and long-term business growth. Our team combines modern development practices with SEO-friendly coding, responsive design, and user-focused experiences to create websites that help businesses stand out online.</p>
          </div>
          <div className="why-bento-grid">
            <div className="why-bento-item main glass animate-fade-up">
              <div className="why-bento-content">
                <div className="why-bento-badge">98% Retention</div>
                <h3>Building Partnerships,<br />Not Just Products</h3>
                <p>We work as an extension of your internal team, providing full visibility into every sprint and milestone.</p>
                <Link to="/about" className="why-learn-more" aria-label="Learn more about ZenTera Tech">
                  Learn more <ArrowRight />
                </Link>
              </div>
              <div className="why-bento-image-bg">
                <img src={Partnership} alt="Partnership" width="350" height="350" />
              </div>
            </div>

            <div className="why-bento-item feature purple-feature animate-fade-up">
              <div className="why-bento-content">
                <div className="why-feat-image">
                  <img src={Engineering} alt="Engineering" width="64" height="64" />
                </div>
                <h3>Future-Proof Engineering</h3>
                <p>Scaling with technologies that evolve with your business, built to last.</p>
              </div>
              <div className="why-bento-number">01</div>
            </div>

            <div className="why-bento-item feature cyan-feature animate-fade-up">
              <div className="why-bento-content">
                <div className="why-feat-image">
                  <img src={AI} alt="AI" width="64" height="64" />
                </div>
                <h3>AI-Native Workflow</h3>
                <p>Smarter, faster development through deeply integrated AI at every layer.</p>
              </div>
              <div className="why-bento-number">02</div>
            </div>

            <div className="why-bento-item wide glass-feature animate-fade-up">
              <div className="why-bento-content">
                <h3>Cloud-First Architecture</h3>
                <p>Leveraging AWS, GCP and Azure for infinitely scalable, enterprise-grade infrastructure that never fails under pressure.</p>
                <div className="cloud-logos">
                  <span className="cloud-tag">AWS</span>
                  <span className="cloud-tag">GCP</span>
                  <span className="cloud-tag">AZURE</span>
                </div>
              </div>
              <div className="why-bento-image-full">
                <img src={Cloud} alt="Cloud" width="600" height="600" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Featured Projects ---- */}
      <section className="section">
        <div className="container">
          <div className="section-header center">
            <h2 className="badge new-h2">Our Work</h2>
            <p className="section-title">Projects That <span className="highlight">Speak for Themselves</span></p>
            <p className="section-subtitle">Our work reflects a combination of creativity, technical expertise, and a results-driven approach. At Zentera Tech, we have worked on custom business websites, ecommerce stores, and modern web solutions designed to improve user experience and online visibility.</p>
          </div>
          <div className="grid-3">
            {projectsData.slice(0, 3).map(p => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
          <div className="section-cta">
            <Link to="/projects" className="btn-outline">See All Projects</Link>
          </div>
        </div>
      </section>

      {/* ---- Testimonials ---- */}
      <section className="section testimonials-section">
        <div className="container">
          <div className="section-header center">
            <span className="badge">Client Stories</span>
            <h2 className="section-title">Trusted by <span className="highlight">Industry Leaders</span></h2>
            <p className="section-subtitle">Don't take our word for it — hear from the clients who've experienced the ZenTera difference.</p>
          </div>
          <div className="grid-3">
            {testimonialsData.slice(0, 3).map(t => (
              <TestimonialCard key={t.id} testimonial={t} />
            ))}
          </div>
          <div className="section-cta">
            <Link to="/testimonials" className="btn-outline">Read All Reviews</Link>
          </div>
        </div>
      </section>


      {/* ---- CTA Banner ---- */}
      <section className="cta-banner-section">
        <div className="container">
          <div className="cta-banner">
            <div className="cta-banner-glow animate-float-slow" />
            <div className="cta-banner-grid-lines" />
            <div className="cta-banner-content">
              <div className="cta-banner-eyebrow">
                <span className="cta-live-dot" />
                Available for new projects
              </div>
              <h2 className="cta-banner-title">
                Ready to Build Something <span className="highlight">Extraordinary?</span>
              </h2>
              <p className="cta-banner-subtitle">
                Let's talk about your project. Our team is ready to turn your vision into reality.
              </p>
              <div className="cta-banner-actions">
                <Link to="/contact" className="btn-primary">Start Your Project <ArrowRight /></Link>
                <Link to="/about" className="btn-outline cta-btn-outline">Learn About Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* ---- FAQ ---- */}
      <Faq />


      {/* ---- Blog ---- */}
      <section className="section blog-section">
        <div className="container" style={{maxWidth:'760px'}}>
          <div className="section-header center">
            <h2 className="badge new-h2">Our Blog</h2>
            <p className="section-title">Insights & <span className="highlight">Web Dev Tips</span></p>
            <p className="section-subtitle">
              Stay ahead with expert articles on web development, design trends, and digital growth strategies.
            </p>
          </div>
          <div className="blog-grid">
           {[...blogData].reverse().slice(0, 4).map((blog) => (
  <BlogCard key={blog.id} blog={blog} />
))}
          </div>
          <div className="section-cta">
            <Link to="/blogs" className="btn-outline">
              View All Articles
              <ArrowRight />
            </Link>
          </div>
        </div>
      </section>


    </main>
  );
}